import React, { useId } from "react";
import { cn } from "./utils";

const SIZE_STYLES = {
  default: {
    wrapper: "px-2.5 py-1.5",
    label: "text-[10px]",
    input: "mt-1 text-base",
  },
  compact: {
    wrapper: "px-2 py-1",
    label: "text-[9px]",
    input: "mt-0.5 text-sm",
  },
};

const handleBoundaryNavigation = (event, onMovePrevious, onMoveNext) => {
  const { selectionStart, selectionEnd, value } = event.currentTarget;
  const cursorStart = selectionStart ?? 0;
  const cursorEnd = selectionEnd ?? 0;

  if (
    event.key === "ArrowLeft" &&
    cursorStart === 0 &&
    cursorEnd === 0 &&
    onMovePrevious
  ) {
    event.preventDefault();
    onMovePrevious();
  }

  if (
    event.key === "ArrowRight" &&
    cursorStart === value.length &&
    cursorEnd === value.length &&
    onMoveNext
  ) {
    event.preventDefault();
    onMoveNext();
  }
};

const SpinnerField = React.forwardRef(function SpinnerField(
  {
    label,
    value,
    onChange,
    onCommit,
    onIncrement,
    onDecrement,
    onMovePrevious,
    onMoveNext,
    onKeyDown,
    onClick,
    inputMode = "numeric",
    maxLength,
    disabled = false,
    invalid = false,
    size = "default",
    className,
    inputClassName,
    ariaLabel,
  },
  ref,
) {
  const id = useId();
  const styles = SIZE_STYLES[size] ?? SIZE_STYLES.default;

  return (
    <div
      className={cn(
        "min-w-0 w-full rounded-md border border-border-subtle bg-bg-tertiary/60",
        styles.wrapper,
        invalid && "border-destructive/60 bg-destructive/5",
        disabled && "opacity-50",
        className,
      )}
    >
      <label
        htmlFor={id}
        className={cn(
          "block font-semibold uppercase tracking-[0.16em] text-text-tertiary",
          styles.label,
        )}
      >
        {label}
      </label>
      <input
        id={id}
        ref={ref}
        type="text"
        value={value}
        inputMode={inputMode}
        maxLength={maxLength}
        disabled={disabled}
        aria-label={ariaLabel ?? label}
        className={cn(
          "min-w-0 w-full border-0 bg-transparent px-0 pb-1 font-medium tabular-nums text-text-primary shadow-none outline-none transition-colors placeholder:text-text-tertiary/50",
          styles.input,
          inputClassName,
        )}
        onChange={(event) => onChange?.(event.target.value)}
        onFocus={(event) => {
          event.currentTarget.select();
        }}
        onBlur={() => onCommit?.()}
        onClick={() => onClick?.()}
        onKeyDown={(event) => {
          if (event.key === "ArrowUp" && onIncrement) {
            event.preventDefault();
            onIncrement();
            return;
          }

          if (event.key === "ArrowDown" && onDecrement) {
            event.preventDefault();
            onDecrement();
            return;
          }

          handleBoundaryNavigation(event, onMovePrevious, onMoveNext);
          onKeyDown?.(event);
        }}
      />
    </div>
  );
});

export default SpinnerField;
